/**
 * 指挥页救援目标 / 轨迹上图（从 Dashboard 拆出）
 */
import mapboxgl from 'mapbox-gl';
import { createRescueIcon, escapeHtml } from './rescueMarkers';
import { toMapLngLat } from './basemapCoord';

export function buildRescueTargetPopupHtml(targetData = {}) {
  return `
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #d32f2f;">🚨 ${escapeHtml(targetData.message || '救援目标')}</h4>
            <p style="margin: 4px 0;"><strong>描述:</strong> ${escapeHtml(targetData.description || '无描述')}</p>
            <p style="margin: 4px 0;"><strong>级别:</strong> ${escapeHtml(targetData.level || '普通')}</p>
            <p style="margin: 4px 0;"><strong>坐标:</strong> ${targetData.lat?.toFixed?.(6)}, ${targetData.lng?.toFixed?.(6)}</p>
            <p style="margin: 4px 0;"><strong>时间:</strong> ${targetData.created_at ? new Date(targetData.created_at).toLocaleString() : ''}</p>
            <p style="margin: 4px 0;"><strong>操作人员:</strong> ${escapeHtml(targetData.operator_name || '系统管理员')}</p>
          </div>
        `;
}

export function buildRescueTrackPopupHtml(trackData = {}, coordinates = []) {
  return `
            <div style="min-width: 200px;">
              <h4 style="margin: 0 0 8px 0; color: #d32f2f;">📍 ${escapeHtml(trackData.message || '救援轨迹')}</h4>
              <p style="margin: 4px 0;"><strong>轨迹点数:</strong> ${coordinates.length}</p>
              <p style="margin: 4px 0;"><strong>创建时间:</strong> ${trackData.created_at ? new Date(trackData.created_at).toLocaleString() : ''}</p>
              <p style="margin: 4px 0;"><strong>操作人员:</strong> ${escapeHtml(trackData.operator_name || '系统管理员')}</p>
            </div>
          `;
}

export function buildRescueTrackPointPopupHtml(trackData = {}, coord = {}, index = 0) {
  return `
            <div style="min-width: 180px;">
              <h4 style="margin: 0 0 8px 0; color: #d32f2f;">📍 轨迹点 ${index + 1}</h4>
              <p style="margin: 4px 0;"><strong>消息:</strong> ${escapeHtml(trackData.message || '无')}</p>
              <p style="margin: 4px 0;"><strong>坐标:</strong> ${coord.lat?.toFixed?.(6)}, ${coord.lng?.toFixed?.(6)}</p>
              ${coord.time ? `<p style="margin: 4px 0;"><strong>时间:</strong> ${new Date(coord.time).toLocaleString()}</p>` : ''}
              <p style="margin: 4px 0;"><strong>操作人员:</strong> ${escapeHtml(trackData.operator_name || '系统管理员')}</p>
            </div>
          `;
}

export function showRescueTargetHoverTooltip(targetData, element) {
  const tooltip = document.createElement('div');
  tooltip.className = 'rescue-target-tooltip';
  tooltip.style.cssText = `
      position: absolute;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 6px 10px;
      border-radius: 4px;
      font-size: 11px;
      z-index: 1000;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;
  tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 2px;">🚨 ${escapeHtml(targetData.message || '救援目标')}</div>
      <div style="font-size: 10px;">描述: ${escapeHtml(targetData.description || '无描述')}</div>
      <div style="font-size: 10px;">时间: ${targetData.created_at ? new Date(targetData.created_at).toLocaleString() : ''}</div>
    `;
  document.body.appendChild(tooltip);
  const rect = element.getBoundingClientRect();
  tooltip.style.left = `${rect.left}px`;
  tooltip.style.top = `${rect.bottom + 5}px`;
  return tooltip;
}

function readProjectionName(map) {
  try {
    return map?.getProjection?.()?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}
export function showRescueTrackHoverTooltip(trackData, element) {
  const tooltip = document.createElement('div');
  tooltip.className = 'rescue-track-tooltip';
  tooltip.style.cssText = `
      position: absolute;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 6px 10px;
      border-radius: 4px;
      font-size: 11px;
      z-index: 1000;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;
  tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 2px;">📍 ${escapeHtml(trackData.message || '救援轨迹')}</div>
      <div style="font-size: 10px;">点数: ${trackData.coordinates?.length || 0}</div>
      <div style="font-size: 10px;">时间: ${trackData.created_at ? new Date(trackData.created_at).toLocaleString() : ''}</div>
    `;
  document.body.appendChild(tooltip);
  const rect = element.getBoundingClientRect();
  tooltip.style.left = `${rect.left}px`;
  tooltip.style.top = `${rect.bottom + 5}px`;
  return tooltip;
}

function positionHoverListTooltip(tooltip, buttonElement) {
  const rect = buttonElement.getBoundingClientRect();
  tooltip.style.left = `${rect.left}px`;
  tooltip.style.top = `${rect.bottom + 5}px`;
  const tooltipRect = tooltip.getBoundingClientRect();
  if (tooltipRect.right > window.innerWidth) {
    tooltip.style.left = `${window.innerWidth - tooltipRect.width - 10}px`;
  }
  if (tooltipRect.bottom > window.innerHeight) {
    tooltip.style.top = `${rect.top - tooltipRect.height - 5}px`;
  }
  tooltip.addEventListener('mouseleave', () => {
    tooltip.remove();
  });
}

/**
 * 救援目标按钮悬停列表
 * @param {HTMLElement} buttonElement
 * @param {Array} rescueTargets
 */
export function showRescueTargetsListTooltip(buttonElement, rescueTargets = []) {
  document.querySelectorAll('.rescue-targets-list-tooltip').forEach((tooltip) => {
    if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
  });

  const tooltip = document.createElement('div');
  tooltip.className = 'rescue-targets-list-tooltip';
  tooltip.style.cssText = `
      position: absolute;
      background: rgba(255, 255, 255, 0.95);
      color: #333;
      padding: 12px;
      border-radius: 8px;
      font-size: 12px;
      z-index: 1000;
      pointer-events: auto;
      max-width: 300px;
      max-height: 200px;
      overflow-y: auto;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      border: 1px solid #e0e0e0;
    `;

  const targetsList = rescueTargets.map((target) => {
    const urgent = target.level === '紧急';
    const border = urgent ? '#d32f2f' : '#1976d2';
    const bg = urgent ? 'rgba(211, 47, 47, 0.1)' : 'rgba(25, 118, 210, 0.1)';
    const bgHover = urgent ? 'rgba(211, 47, 47, 0.2)' : 'rgba(25, 118, 210, 0.2)';
    const lat = Number(target.lat) || 0;
    const lng = Number(target.lng) || 0;
    return `
      <div 
        onclick="window.flyToRescueTarget(${lat}, ${lng})"
        style="margin-bottom: 8px; padding: 8px; border-left: 3px solid ${border}; background: ${bg}; border-radius: 4px; cursor: pointer; transition: all 0.2s ease;"
        onmouseover="this.style.background='${bgHover}'; this.style.transform='translateX(2px)'"
        onmouseout="this.style.background='${bg}'; this.style.transform='translateX(0)'"
      >
        <div style="font-weight: bold; margin-bottom: 4px; color: #333;">🚨 ${escapeHtml(target.message || '救援目标')}</div>
        <div style="font-size: 11px; color: #666; margin-bottom: 2px;">描述: ${escapeHtml(target.description || '无描述')}</div>
        <div style="font-size: 11px; color: #666;">时间: ${target.created_at ? new Date(target.created_at).toLocaleString() : ''}</div>
        <div style="font-size: 11px; color: #999;">操作人员: ${escapeHtml(target.operator_name || '系统管理员')}</div>
      </div>
    `;
  }).join('');

  tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; color: #1976d2; font-size: 13px;">救援目标列表 (${rescueTargets.length}个)</div>
      ${targetsList}
      <div style="font-size: 11px; color: #999; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e0e0;">点击列表项可飞行到目标位置</div>
    `;
  document.body.appendChild(tooltip);
  positionHoverListTooltip(tooltip, buttonElement);
  return tooltip;
}

/**
 * 救援轨迹按钮悬停列表
 * @param {HTMLElement} buttonElement
 * @param {Array} rescueTracks
 */
export function showRescueTracksListTooltip(buttonElement, rescueTracks = []) {
  document.querySelectorAll('.rescue-tracks-list-tooltip').forEach((tooltip) => {
    if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
  });

  const tooltip = document.createElement('div');
  tooltip.className = 'rescue-tracks-list-tooltip';
  tooltip.style.cssText = `
      position: absolute;
      background: rgba(255, 255, 255, 0.95);
      color: #333;
      padding: 12px;
      border-radius: 8px;
      font-size: 12px;
      z-index: 1000;
      pointer-events: auto;
      max-width: 300px;
      max-height: 200px;
      overflow-y: auto;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      border: 1px solid #e0e0e0;
    `;

  const tracksList = rescueTracks.map((track) => {
    const startCoord = track.coordinates && track.coordinates.length > 0 ? track.coordinates[0] : null;
    const lat = startCoord ? Number(startCoord.lat) || 0 : 0;
    const lng = startCoord ? Number(startCoord.lng) || 0 : 0;
    return `
        <div 
          onclick="window.flyToRescueTrack(${lat}, ${lng})"
          style="margin-bottom: 8px; padding: 8px; border-left: 3px solid #d32f2f; background: rgba(211, 47, 47, 0.1); border-radius: 4px; cursor: pointer; transition: all 0.2s ease;"
          onmouseover="this.style.background='rgba(211, 47, 47, 0.2)'; this.style.transform='translateX(2px)'"
          onmouseout="this.style.background='rgba(211, 47, 47, 0.1)'; this.style.transform='translateX(0)'"
        >
          <div style="font-weight: bold; margin-bottom: 4px; color: #333;">📍 ${escapeHtml(track.message || '救援轨迹')}</div>
          <div style="font-size: 11px; color: #666;">轨迹点数: ${track.coordinates?.length || 0}</div>
          <div style="font-size: 11px; color: #666;">时间: ${track.created_at ? new Date(track.created_at).toLocaleString() : ''}</div>
          <div style="font-size: 11px; color: #999;">操作人员: ${escapeHtml(track.operator_name || '系统管理员')}</div>
        </div>
      `;
  }).join('');

  tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; color: #1976d2; font-size: 13px;">救援轨迹列表 (${rescueTracks.length}个)</div>
      ${tracksList}
      <div style="font-size: 11px; color: #999; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e0e0;">点击列表项可飞行到轨迹起始位置</div>
    `;
  document.body.appendChild(tooltip);
  positionHoverListTooltip(tooltip, buttonElement);
  return tooltip;
}

/**
 * @param {mapboxgl.Map} map
 * @param {object} targetData
 * @param {{
 *   markersRef: { current: any[] },
 *   onHoverShowList?: (targetData, el) => HTMLElement|null,
 *   displayLngLat?: [number, number]
 * }} options
 */
export function displayRescueTargetOnMap(map, targetData, options = {}) {
  if (!map || !targetData) return null;
const projection = readProjectionName(map);
  if (projection === 'globe') {
    try {
      if (typeof map.setProjection === 'function') map.setProjection('mercator');
    } catch (_) {
      return null;
    }
  }


  const { markersRef, onHoverShowList, displayLngLat: displayLngLatOverride } = options;
  const markerId = `rescue-target-${targetData.id}`;

  if (markersRef?.current) {
    const existingMarker = markersRef.current.find((m) => m.id === markerId);
    if (existingMarker) {
      try {
        existingMarker.marker?.remove?.();
      } catch (_) {
        /* ignore */
      }
      markersRef.current = markersRef.current.filter((m) => m.id !== markerId);
    }
  }

  const iconColor = targetData.level === '紧急' ? '#ff0000' : '#1976d2';
  // 紧急显示 SOS；普通为空心圆点。消息改走悬停/弹窗，避免针上方气泡被压成「细线」
  const el = createRescueIcon(iconColor, targetData.message || '', {
    badge: targetData.level === '紧急' ? 'SOS' : ''
  });
  if (targetData.message) el.title = String(targetData.message);

  let tooltip = null;
  el.addEventListener('mouseenter', () => {
    tooltip = onHoverShowList
      ? onHoverShowList(targetData, el)
      : showRescueTargetHoverTooltip(targetData, el);
  });
  el.addEventListener('mouseleave', () => {
    if (tooltip?.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
      tooltip = null;
    }
  });

  // 优先用种点时的显示坐标，避免 WGS↔GCJ 往返产生亚米级漂移
  const displayLngLat = (Array.isArray(displayLngLatOverride) && displayLngLatOverride.length >= 2
    && Number.isFinite(Number(displayLngLatOverride[0]))
    && Number.isFinite(Number(displayLngLatOverride[1])))
    ? [Number(displayLngLatOverride[0]), Number(displayLngLatOverride[1])]
    : (toMapLngLat(targetData.lng, targetData.lat, undefined, map) || [targetData.lng, targetData.lat]);

  // 水滴针尖在图标底边中点，须用 bottom（与种点预览一致）；center 会导致确认后上移半个图标
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport'
  })
    .setLngLat(displayLngLat)
    .addTo(map);

  el.addEventListener('click', () => {
    map.flyTo({
      center: displayLngLat,
      zoom: 16,
      duration: 2000,
      essential: true
    });
    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(buildRescueTargetPopupHtml(targetData));
    marker.setPopup(popup);
    marker.togglePopup();
  });

  if (markersRef?.current) {
    markersRef.current.push({
      id: markerId,
      marker,
      data: targetData
    });
  }

  return marker;
}

/**
 * 救援推送轨迹视觉规范（卫星底图友好）：
 * - 亮警示红主体 + 深色细描边，远近缩放都清晰
 * - 白色人字箭头嵌在线内，间距适中，不抢队员/目标标记
 */
export const RESCUE_TRACK_LINE_COLOR = '#e53935';
export const RESCUE_TRACK_LINE_CASING = '#1a1a1a';
/** 换图后改 id，避免旧图标被 hasImage 缓存 */
export const RESCUE_TRACK_ARROW_IMAGE_ID = 'zhxt-rescue-track-chevron-v6';

/** 线宽随缩放：略细，箭头笔画粗但不撑破线宽 */
const RESCUE_TRACK_LINE_WIDTH = [
  'interpolate', ['linear'], ['zoom'],
  8, 2.6,
  12, 4,
  16, 5.2,
  18, 6
];
const RESCUE_TRACK_CASING_WIDTH = [
  'interpolate', ['linear'], ['zoom'],
  8, 4.2,
  12, 5.8,
  16, 7.2,
  18, 8.2
];

/**
 * 注册白色尖角人字（2～3 道叠画加粗），透明底嵌在红线内。
 * @param {mapboxgl.Map} map
 */
export function ensureRescueTrackArrowImage(map) {
  if (!map || typeof map.addImage !== 'function') return RESCUE_TRACK_ARROW_IMAGE_ID;

  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return RESCUE_TRACK_ARROW_IMAGE_ID;

  ctx.clearRect(0, 0, size, size);

  /**
   * 尖角人字：纵向张角小 → 看起来更尖；笔画用多道叠绘加粗
   * @param {string} color
   * @param {number} width
   * @param {number} [shiftX] 沿前进方向微移，叠出“两三个人字”的厚度
   */
  const drawChevron = (color, width, shiftX = 0) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    ctx.miterLimit = 3;
    const tipX = size * 0.62 + shiftX;
    const cy = size / 2;
    // 张角收小：上下臂只张开约 ±14px（在 96 画布上），尖而窄
    const backX = tipX - size * 0.28;
    const halfOpen = size * 0.145;
    ctx.beginPath();
    ctx.moveTo(backX, cy - halfOpen);
    ctx.lineTo(tipX, cy);
    ctx.lineTo(backX, cy + halfOpen);
    ctx.stroke();
  };

  // 三道沿前进方向微叠，等效 2～3 个人字拼在一起的粗细
  const offsets = [-7, 0, 7];
  offsets.forEach((dx) => drawChevron('rgba(0,0,0,0.35)', 14, dx));
  offsets.forEach((dx) => drawChevron('#ffffff', 11, dx));

  const imageData = ctx.getImageData(0, 0, size, size);
  try {
    // 强制换图，避免旧细箭头一直留在 map 图集里
    if (map.hasImage?.(RESCUE_TRACK_ARROW_IMAGE_ID)) {
      try { map.removeImage(RESCUE_TRACK_ARROW_IMAGE_ID); } catch (_) { /* ignore */ }
    }
    map.addImage(RESCUE_TRACK_ARROW_IMAGE_ID, imageData, { pixelRatio: 2 });
  } catch (error) {
    console.warn('注册救援轨迹箭头图标失败:', error);
  }
  return RESCUE_TRACK_ARROW_IMAGE_ID;
}

/**
 * 描边红线 + 白色人字箭头（起点→终点）
 * @param {mapboxgl.Map} map
 * @param {string} trackId
 * @param {{
 *   lineColor?: string,
 *   casingColor?: string,
 *   lineWidth?: number|Array,
 *   casingWidth?: number|Array,
 *   spacing?: number,
 *   iconSize?: number,
 *   withArrows?: boolean
 * }} [options]
 */
export function styleRescueTrackLineLayers(map, trackId, options = {}) {
  if (!map || !trackId || !map.getSource?.(trackId)) return null;

  const lineColor = options.lineColor || RESCUE_TRACK_LINE_COLOR;
  const casingColor = options.casingColor || RESCUE_TRACK_LINE_CASING;
  const lineWidth = options.lineWidth != null ? options.lineWidth : RESCUE_TRACK_LINE_WIDTH;
  const casingWidth = options.casingWidth != null ? options.casingWidth : RESCUE_TRACK_CASING_WIDTH;
  const withArrows = options.withArrows !== false;

  [`${trackId}-arrows`, `${trackId}-line`, `${trackId}-casing`].forEach((layerId) => {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    } catch (_) {
      /* ignore */
    }
  });

  // 深色细描边：卫星/街道底图上都有轮廓
  map.addLayer({
    id: `${trackId}-casing`,
    type: 'line',
    source: trackId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': casingColor,
      'line-width': casingWidth,
      'line-opacity': 0.55
    }
  });

  map.addLayer({
    id: `${trackId}-line`,
    type: 'line',
    source: trackId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': lineColor,
      'line-width': lineWidth,
      'line-opacity': 0.92
    }
  });

  if (withArrows) {
    addRescueTrackArrowLayer(map, trackId, {
      spacing: options.spacing,
      size: options.iconSize
    });
  }

  return {
    casingId: `${trackId}-casing`,
    lineId: `${trackId}-line`,
    arrowsId: `${trackId}-arrows`
  };
}

/**
 * 在轨迹线上叠加方向箭头图层（起点→终点）
 * @param {mapboxgl.Map} map
 * @param {string} trackId 与 line source 相同 id
 * @param {{ spacing?: number, size?: number }} [options]
 */
export function addRescueTrackArrowLayer(map, trackId, options = {}) {
  if (!map || !trackId || !map.getSource?.(trackId)) return null;
  const layerId = `${trackId}-arrows`;
  try {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  } catch (_) {
    /* ignore */
  }

  ensureRescueTrackArrowImage(map);
  // 图标高度压在线宽内；粗细靠三道叠画，不靠放大
  const spacing = Number(options.spacing) > 0 ? Number(options.spacing) : 32;
  const size = Number(options.size) > 0 ? Number(options.size) : 0.26;

  map.addLayer({
    id: layerId,
    type: 'symbol',
    source: trackId,
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': spacing,
      'icon-image': RESCUE_TRACK_ARROW_IMAGE_ID,
      'icon-size': size,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-rotation-alignment': 'map',
      'icon-pitch-alignment': 'map'
    },
    paint: {
      'icon-opacity': 0.94
    }
  });
  return layerId;
}

function removeRescueTrackMapLayers(map, trackId) {
  if (!map || !trackId) return;
  [`${trackId}-arrows`, `${trackId}-line`, `${trackId}-casing`].forEach((layerId) => {
    try {
      if (map.getLayer?.(layerId)) map.removeLayer(layerId);
    } catch (_) {
      /* ignore */
    }
  });
  try {
    if (map.getSource?.(trackId)) map.removeSource(trackId);
  } catch (_) {
    /* ignore */
  }
}

/** @param {string|{ id: string, markers?: mapboxgl.Marker[] }} entry */
function normalizeTrackLineEntry(entry) {
  if (typeof entry === 'string') return { id: entry, markers: [] };
  if (entry && typeof entry.id === 'string') {
    return { id: entry.id, markers: Array.isArray(entry.markers) ? entry.markers : [] };
  }
  return null;
}

function removeTrackEndpointMarkers(markers = []) {
  markers.forEach((marker) => {
    try { marker?.remove?.(); } catch (_) { /* ignore */ }
  });
}

function createTrackEndpointMarker(map, lngLat, { label, color, popupHtml, onHoverShowList, trackData }) {
  const el = document.createElement('div');
  el.className = 'rescue-track-endpoint-marker';
  el.title = label;
  el.style.width = '12px';
  el.style.height = '12px';
  el.style.borderRadius = '50%';
  el.style.backgroundColor = color;
  el.style.border = '2px solid white';
  el.style.cursor = 'pointer';
  el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)';

  let tooltip = null;
  el.addEventListener('mouseenter', () => {
    tooltip = onHoverShowList ? onHoverShowList(trackData, el) : null;
  });
  el.addEventListener('mouseleave', () => {
    if (tooltip?.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
      tooltip = null;
    }
  });

  return new mapboxgl.Marker(el)
    .setLngLat(lngLat)
    .setPopup(new mapboxgl.Popup({ offset: 18 }).setHTML(popupHtml))
    .addTo(map);
}

/**
 * @param {mapboxgl.Map} map
 * @param {object} trackData
 * @param {{
 *   trackLinesRef?: { current: Array<string|{ id: string, markers?: mapboxgl.Marker[] }> },
 *   onHoverShowList?: (trackData, el) => HTMLElement|null
 * }} options
 */
export function displayRescueTrackOnMap(map, trackData, options = {}) {
  if (!map || !trackData?.coordinates?.length) return null;
const projection = readProjectionName(map);
  if (projection === 'globe') {
    try {
      if (typeof map.setProjection === 'function') map.setProjection('mercator');
    } catch (_) {
      return null;
    }
  }


  const { trackLinesRef, onHoverShowList } = options;
  const trackId = `rescue-track-${trackData.id}`;
  const coordinates = trackData.coordinates.map(
    (coord) => toMapLngLat(coord.lng, coord.lat, undefined, map) || [coord.lng, coord.lat]
  );

  // 重绘前先清掉旧线与起终点标记
  if (trackLinesRef?.current) {
    const existing = trackLinesRef.current.find((entry) => normalizeTrackLineEntry(entry)?.id === trackId);
    const normalized = normalizeTrackLineEntry(existing);
    if (normalized) removeTrackEndpointMarkers(normalized.markers);
    trackLinesRef.current = trackLinesRef.current.filter(
      (entry) => normalizeTrackLineEntry(entry)?.id !== trackId
    );
  }

  removeRescueTrackMapLayers(map, trackId);

  map.addSource(trackId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        message: trackData.message,
        icon: trackData.icon
      },
      geometry: {
        type: 'LineString',
        coordinates
      }
    }
  });

  // 粗红带 + 白色 >>> 人字箭头（嵌在线内，指向终点）
  styleRescueTrackLineLayers(map, trackId, {
    withArrows: coordinates.length >= 2
  });

  let lineTooltip = null;
  map.on('mouseenter', `${trackId}-line`, (e) => {
    map.getCanvas().style.cursor = 'pointer';
    if (!onHoverShowList) return;
    const tempEl = document.createElement('div');
    tempEl.style.position = 'absolute';
    tempEl.style.left = `${e.point.x}px`;
    tempEl.style.top = `${e.point.y}px`;
    document.body.appendChild(tempEl);
    lineTooltip = onHoverShowList(trackData, tempEl);
    document.body.removeChild(tempEl);
  });
  map.on('mouseleave', `${trackId}-line`, () => {
    map.getCanvas().style.cursor = '';
    if (lineTooltip?.parentNode) {
      lineTooltip.parentNode.removeChild(lineTooltip);
      lineTooltip = null;
    }
  });
  map.on('click', `${trackId}-line`, () => {
    if (!coordinates.length) return;
    const startPoint = coordinates[0];
    map.flyTo({
      center: startPoint,
      zoom: 14,
      duration: 2000,
      essential: true
    });
    new mapboxgl.Popup()
      .setLngLat(startPoint)
      .setHTML(buildRescueTrackPopupHtml(trackData, coordinates))
      .addTo(map);
  });

  // 只标起终点，中间点由 LineString 连成线（避免导出轨迹推送后变成密密麻麻小点）
  const endpointMarkers = [];
  if (coordinates.length >= 1) {
    const startCoord = trackData.coordinates[0] || {};
    endpointMarkers.push(createTrackEndpointMarker(map, coordinates[0], {
      label: '起点',
      color: '#2e7d32',
      popupHtml: buildRescueTrackPointPopupHtml(trackData, startCoord, 0),
      onHoverShowList,
      trackData
    }));
  }
  if (coordinates.length >= 2) {
    const endIndex = trackData.coordinates.length - 1;
    const endCoord = trackData.coordinates[endIndex] || {};
    endpointMarkers.push(createTrackEndpointMarker(map, coordinates[coordinates.length - 1], {
      label: '终点',
      color: '#c62828',
      popupHtml: buildRescueTrackPointPopupHtml(trackData, endCoord, endIndex),
      onHoverShowList,
      trackData
    }));
  }

  if (trackLinesRef?.current) {
    trackLinesRef.current.push({ id: trackId, markers: endpointMarkers });
  }
  return trackId;
}

/**
 * 清理地图上的救援目标标记与轨迹图层
 */
export function clearRescueDataFromMap(map, markersRef, trackLinesRef) {
  if (markersRef?.current) {
    markersRef.current.forEach((markerRef) => {
      try {
        if (markerRef?.marker?.remove) markerRef.marker.remove();
      } catch (error) {
        console.log('清理救援目标标记时出错:', error);
      }
    });
    markersRef.current = [];
  }

  if (map && trackLinesRef?.current) {
    trackLinesRef.current.forEach((entry) => {
      const normalized = normalizeTrackLineEntry(entry);
      if (!normalized) return;
      removeTrackEndpointMarkers(normalized.markers);
      try {
        removeRescueTrackMapLayers(map, normalized.id);
      } catch (error) {
        console.log('清理救援轨迹时出错:', error);
      }
    });
    trackLinesRef.current = [];
  }
}

export function removeRescueTargetMarkerFromMap(markersRef, targetId) {
  if (!targetId || !markersRef?.current) return false;
  const markerId = `rescue-target-${targetId}`;
  const existingMarker = markersRef.current.find((m) => m.id === markerId);
  if (!existingMarker) return false;
  try {
    existingMarker.marker.remove();
  } catch (error) {
    console.log('移除救援目标标记失败:', error);
  }
  markersRef.current = markersRef.current.filter((m) => m.id !== markerId);
  return true;
}

export function removeRescueTrackFromMap(map, trackLinesRef, trackIdOrDataId) {
  if (!map || !trackIdOrDataId) return false;
  const trackId = String(trackIdOrDataId).startsWith('rescue-track-')
    ? String(trackIdOrDataId)
    : `rescue-track-${trackIdOrDataId}`;

  if (trackLinesRef?.current) {
    const existing = trackLinesRef.current.find((entry) => normalizeTrackLineEntry(entry)?.id === trackId);
    const normalized = normalizeTrackLineEntry(existing);
    if (normalized) removeTrackEndpointMarkers(normalized.markers);
    trackLinesRef.current = trackLinesRef.current.filter(
      (entry) => normalizeTrackLineEntry(entry)?.id !== trackId
    );
  }

  try {
    removeRescueTrackMapLayers(map, trackId);
  } catch (error) {
    console.log('移除救援轨迹失败:', error);
  }
  return true;
}
